<template>
  <div class="inventory-page">
    <block-buttons
      title="Инвентаризация"
      description="Сканируйте товары и сверяйте фактическое количество с учётным"
      :actions="actions"
    >
      <template #icon>
        <ClipboardCheck :size="22" />
      </template>
    </block-buttons>

    <div
      class="inventory-page__status"
      role="status"
    >
      {{ getStatusText }}
      <span>Черновик хранится в этом браузере. Остатки в каталоге не изменяются.</span>
    </div>

    <section
      class="inventory-page__summary"
      aria-label="Итоги инвентаризации"
    >
      <article>
        <span>Проверено позиций</span>
        <strong>{{ renderData.length }}</strong>
        <small>Осталось проверить: {{ unchecked.length }}</small>
      </article>
      <article>
        <span>Фактическое количество</span>
        <strong>{{ totals.actual }} шт.</strong>
        <small>По учёту в проверенных: {{ totals.expected }} шт.</small>
      </article>
      <article>
        <span>Расхождения</span>
        <strong>{{ discrepancies.length }}</strong>
        <small>Недостача: {{ totals.shortage }} · Излишек: {{ totals.surplus }}</small>
      </article>
      <article>
        <span>Стоимость проверенных</span>
        <strong>{{ priceToFormat(totals.price) }}</strong>
        <small>По закупке: {{ priceToFormat(totals.purchasePrice) }}</small>
      </article>
    </section>

    <section class="inventory-page__card inventory-page__scanner">
      <form @submit.prevent="submitSearch">
        <label for="inventory-search">Добавить товар</label>

        <div class="inventory-page__search">
          <input
            id="inventory-search"
            ref="search"
            v-model="query"
            type="search"
            autocomplete="off"
            placeholder="Штрихкод или название товара"
            :disabled="loading"
            @keydown.esc="query = ''"
          />
          <button
            type="submit"
            :disabled="loading || !query.trim()"
          >
            Добавить +1
          </button>
        </div>
      </form>

      <p class="inventory-page__hint">Каждое сканирование добавляет одну единицу. Точное количество можно указать в таблице, включая 0.</p>

      <p
        class="inventory-page__feedback"
        role="status"
      >
        {{ feedback }}
      </p>

      <div
        v-if="isVisibleResult"
        class="inventory-page__results"
      >
        <button
          v-for="product in matches"
          :key="product.code"
          type="button"
          @click="addProduct(product)"
        >
          <span>
            <strong>{{ product.title }}</strong>
            <small>{{ product.code }} · {{ product.categoryName }}</small>
          </span>

          <span>Остаток: {{ product.count }} · +1</span>
        </button>

        <p v-if="!matches.length">Товары не найдены. Проверьте название или штрихкод.</p>
        <small v-if="matches.length === 20">Показаны первые 20 совпадений. Уточните запрос.</small>
      </div>
    </section>

    <section
      v-if="missingCodes.length"
      class="inventory-page__card inventory-page__missing"
      aria-labelledby="inventory-missing-title"
    >
      <div class="inventory-page__missing-header">
        <h3 id="inventory-missing-title">Товары не найдены в каталоге ({{ missingCodes.length }})</h3>

        <button
          type="button"
          @click="clearMissingCodes"
        >
          Очистить
        </button>
      </div>

      <p class="inventory-page__hint">Проверьте эти штрихкоды после пересчёта. Повторные сканирования указаны рядом с кодом.</p>

      <ul>
        <li
          v-for="item in missingCodes"
          :key="item.code"
        >
          <code>{{ item.code }}</code>
          <span>Сканирований: {{ item.count }}</span>
        </li>
      </ul>
    </section>

    <data-table
      :rows="visibleRows"
      :reset-page-on-rows-change="false"
      :columns="tableCheckedColumns"
      row-key="code"
      :search-keys="['title', 'code']"
      title="Проверенные товары"
      description="Учётные остатки и цены фиксируются при добавлении товара"
      search-placeholder="Найти в проверенных"
      empty-text="Добавьте первый товар для начала инвентаризации"
      :loading="loading"
      :error="error"
    >
      <template #filters>
        <label class="inventory-page__filter">
          <input
            v-model="onlyDiscrepancies"
            type="checkbox"
          />

          Только расхождения ({{ discrepancies.length }})
        </label>
      </template>

      <template #cell-title="{ row }">
        <strong>{{ row.title }}</strong>
      </template>

      <template #cell-countNow="{ row }">
        <input
          class="inventory-page__quantity"
          type="number"
          min="0"
          step="1"
          :value="row.countNow"
          :aria-label="`Фактическое количество: ${row.title}`"
          @change="changeQuantity(row, $event)"
          @keydown.enter.prevent="$event.target.blur()"
        />
      </template>

      <template #cell-difference="{ row }">
        <span
          class="inventory-page__difference"
          :class="getClassesPageDifference"
        >
          {{ row.difference > 0 ? '+' : '' }}{{ row.difference }}
        </span>
      </template>

      <template #cell-allPrice="{ row }">{{ priceToFormat(row.allPrice) }}</template>

      <template #cell-allPurchasePrice="{ row }">{{ priceToFormat(row.allPurchasePrice) }}</template>

      <template #cell-actions="{ row }">
        <button
          type="button"
          :aria-label="`Убрать из инвентаризации: ${row.title}`"
          @click="removeProduct(row)"
        >
          Убрать
        </button>
      </template>
    </data-table>

    <section class="inventory-page__remaining">
      <button
        type="button"
        :aria-expanded="isShowUnchecked"
        @click="showUnchecked"
      >
        {{ getShowUncheckedBtnText }} непроверенные товары ({{ unchecked.length }})
      </button>

      <p class="inventory-page__hint">Товары с ненулевым учётным остатком, которые ещё не пересчитаны.</p>
    </section>

    <data-table
      v-if="isShowUnchecked"
      :rows="unchecked"
      :reset-page-on-rows-change="false"
      :columns="tableUncheckedColumns"
      row-key="code"
      :search-keys="['title', 'code']"
      title="Непроверенные товары"
      search-placeholder="Найти непроверенный товар"
      empty-text="Все товары с остатком проверены"
      :loading="loading"
      :error="error"
    >
      <template #cell-actions="{ row }">
        <button
          type="button"
          @click="addProduct(row)"
        >
          Добавить +1
        </button>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ClipboardCheck, Save, RotateCcw } from 'lucide-vue'
import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import { priceToFormat } from '@/helper'

const CACHE_KEY = 'inventoryCache'
const MISSING_CACHE_KEY = 'inventoryMissingCodes'

const number = value => Number.isFinite(Number(value)) ? Number(value) : 0
const validQuantity = value => value !== null && String(value).trim() !== '' && Number.isSafeInteger(Number(value)) && Number(value) >= 0

export default {
  name: 'InventoryV2',
  components: {
    ClipboardCheck,
    BlockButtons,
    DataTable
  },
  inject: {
    pastCodeData: {
      type: Object,
      default: () => ({})
    },
    clearPastCode: {
      type: Function,
      default: () => ({})
    }
  },
  data () {
    return {
      missingCodes: [],
      products: {},
      query: '',
      feedback: '',
      loading: false,
      error: '',
      saveError: '',
      saveStatus: 'Изменения сохраняются автоматически',
      onlyDiscrepancies: false,
      isShowUnchecked: false
    }
  },
  computed: {
    ...mapGetters('product', {
      catalog: 'getData',
      getDataObjectByCode: 'getDataObjectByCode'
    }),
    ...mapGetters('settings', ['getTableColumns']),
    ...mapGetters('config', ['getIsOffline']),
    tableCheckedColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['inventory-checked'] || [])
      ]
    },
    tableUncheckedColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['inventory-unchecked'] || [])
      ]
    },
    actions () {
      return [
        {
          id: 'save',
          label: 'Сохранить',
          icon: Save,
          handler: this.save
        },
        {
          id: 'reset',
          label: 'Новая инвентаризация',
          icon: RotateCcw,
          disabled: !this.renderData.length && !this.missingCodes.length,
          handler: this.confirmReset
        }
      ]
    },
    renderData () {
      return Object.values(this.products).reverse().map(product => ({
        ...product,
        difference: product.countNow - product.count,
        allPrice: product.countNow * product.price,
        allPurchasePrice: product.countNow * product.purchasePrice
      }))
    },
    discrepancies () {
      return this.renderData.filter(row => row.difference !== 0)
    },
    visibleRows () {
      return this.onlyDiscrepancies
        ? this.discrepancies
        : this.renderData
    },
    unchecked () {
      return this.catalog.filter(({ count, code }) => number(count) !== 0 && !Object.prototype.hasOwnProperty.call(this.products, String(code)))
    },
    matches () {
      const words = this.query.trim().toLocaleLowerCase('ru').split(/\s+/)

      return this.catalog.filter(({ title, code }) => words.every(word => `${title} ${code}`.toLocaleLowerCase('ru').includes(word))).slice(0, 20)
    },
    totals () {
      return this.renderData.reduce((total, row) => ({
        actual: total.actual + row.countNow,
        expected: total.expected + row.count,
        shortage: total.shortage + Math.max(0, -row.difference),
        surplus: total.surplus + Math.max(0, row.difference),
        price: total.price + row.allPrice,
        purchasePrice: total.purchasePrice + row.allPurchasePrice
      }), {
        actual: 0,
        expected: 0,
        shortage: 0,
        surplus: 0,
        price: 0,
        purchasePrice: 0
      })
    },
    scannedCodes () {
      return this.pastCodeData.pastCodeList || []
    },
    getStatusText () {
      return this.saveError || this.saveStatus || ''
    },
    isVisibleResult () {
      return this.query.trim() && !this.loading
    },
    getShowUncheckedBtnText () {
      return this.isShowUnchecked ? 'Скрыть' : 'Показать'
    }
  },
  watch: {
    scannedCodes (codes) {
      const code = codes[codes.length - 1]

      if (!code) {
        return
      }

      this.enterCode(code)
      this.clearPastCode()
    }
  },
  created () {
    this.restore()
  },
  methods: {
    ...mapActions('product', { loadDataProduct: 'loadData' }),
    ...mapActions('modal', ['openModal', 'closeModal']),
    priceToFormat,
    submitSearch () {
      const code = this.query.trim()

      if (!code || this.loading) {
        return
      }

      const exact = this.catalog.find(row => String(row.code) === code)

      if (exact) {
        this.addProduct(exact)
      } else if (/^\d+$/.test(code)) {
        this.recordMissingCode(code)
      } else if (this.matches.length === 1) {
        this.addProduct(this.matches[0])
      } else if (this.matches.length) {
        this.feedback = 'Выберите нужный товар из списка.'
      } else {
        this.recordMissingCode(code)
      }
    },
    enterCode (code) {
      code = String(code == null ? '' : code).trim()

      if (!code) {
        return
      }

      if (this.loading) {
        this.feedback = 'Дождитесь загрузки каталога и повторите сканирование.'

        return
      }

      const product = this.getDataObjectByCode[code]
      
      if (product) {
        this.addProduct(product)
        
        return
      } 
      
      this.recordMissingCode(code)
    },
    clearMissingCodes () {
      this.missingCodes = []
      this.feedback = 'Список ненайденных товаров очищен.'
      this.save()
      this.$nextTick(() => this.$refs.search?.focus({ preventScroll: true }))
    },
    recordMissingCode (code) {
      const existing = this.missingCodes.find(item => item.code === code)

      if (existing) {
        existing.count += 1
      } else {
        this.missingCodes.unshift({ code, count: 1 })
      }

      this.feedback = `Товар со штрихкодом ${code} не найден. Код добавлен в список.`
      this.query = ''
      this.save()
      this.$nextTick(() => this.$refs.search?.focus({ preventScroll: true }))

      try {
        const sound = new Audio(require('@/assets/sound/solitary-cry-of-a-pig.mp3'))
        const playback = sound.play()

        if (playback) {
          playback.catch(() => {})
        }
      } catch (_) { /* Keep scanning if audio is unavailable. */ }
    },
    addProduct (product, quantity = 1) {
      if (product.code == null || String(product.code) === '') {
        this.feedback = 'У товара отсутствует штрихкод.'
        return
      }

      const code = String(product.code)
      const previous = Object.prototype.hasOwnProperty.call(this.products, code) ? this.products[code] : null
      const countNow = previous ? previous.countNow + quantity : quantity

      if (!validQuantity(countNow)) {
        return
      }

      const data = previous
        ? { ...previous, countNow }
        : {
            code,
            title: product.title,
            count: number(product.count),
            countNow,
            price: number(product.price),
            purchasePrice: number(product.purchasePrice)
          }

      this.$set(this.products, code, data)

      this.query = ''
      this.feedback = `${product.title}: учтено ${countNow} шт.`
      this.save()
      this.$nextTick(() => this.$refs.search.focus({ preventScroll: true }))
    },
    changeQuantity (row, event) {
      if (!validQuantity(event.target.value)) {
        event.target.value = row.countNow
        this.feedback = 'Введите целое неотрицательное количество.'
        return
      }

      this.$set(this.products, row.code, {
        ...this.products[row.code],
        countNow: Number(event.target.value)
      })
      this.save()
    },
    removeProduct (row) {
      this.$delete(this.products, row.code)
      this.feedback = `${row.title}: убран из инвентаризации.`
      this.save()
    },
    save () {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.products))
      localStorage.setItem(MISSING_CACHE_KEY, JSON.stringify(this.missingCodes))

      this.saveError = ''
      this.saveStatus = `Сохранено в ${new Date().toLocaleTimeString('ru-RU')}`
    },
    restore () {
      this.missingCodes = JSON.parse(localStorage.getItem(MISSING_CACHE_KEY) || '[]')

      const cached = localStorage.getItem(CACHE_KEY)

      if (!cached) {
        return
      }

      const data = JSON.parse(cached)
      const products = {}

      for (const [code, row] of Object.entries(data)) {
        products[code] = {
          code,
          title: row.title,
          count: number(row.count),
          countNow: Number(row.countNow),
          price: number(row.price),
          purchasePrice: number(row.purchasePrice)
        }
      }

      this.products = products
      this.saveStatus = 'Сохранённая инвентаризация восстановлена'
    },
    confirmReset () {
      this.openModal({
        name: 'confirm',
        propsData: {
          type: 'warning',
          title: 'Начать новую инвентаризацию?',
          text: 'Текущий черновик будет очищен. Сохраните необходимые результаты перед продолжением.',
          btnTextCancel: 'Продолжить текущую',
          btnTextConfirm: 'Начать новую',
          cancelCallback: () => this.closeModal('confirm'),
          confirmCallback: () => {
            this.products = {}
            this.missingCodes = []
            this.onlyDiscrepancies = false
            this.isShowUnchecked = false
            this.query = ''
            this.feedback = ''
            this.save()
            this.closeModal('confirm')
          }
        }
      })
    },
    getClassesPageDifference (row) {
      return { 'is-short': row.difference < 0, 'is-over': row.difference > 0 }
    },
    showUnchecked () {
      this.isShowUnchecked = !this.isShowUnchecked
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
